/**
 * ValidationRule Type Definition
 *
 * Represents a rule for data validation in the system.
 */

export interface ValidationRule {
  id: number;
  name: string;
  description: string;
  entityType: string;
  ruleType: "required" | "format" | "range" | "comparison" | "custom" | "unexplained-cash-movement";
  field?: string;
  expression?: string;
  parameters?: Record<string, any>;
  severity: "warning" | "error";
  message: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: number;
  updatedBy?: number;
}

export interface ValidationResult {
  ruleId: number;
  passed: boolean;
  message?: string;
  severity: "warning" | "error";
  fieldName?: string;
  recordId?: string | number;
  details?: Record<string, any>;
}

/**
 * Improved ValidationRuleDto with proper array types
 * This replaces the problematic string-encoded arrays
 */
export interface ImprovedValidationRuleDto {
  validationRuleId?: number | null;
  clientId: number;
  fundId?: number | null;
  ruleName?: string | null;
  ruleType?: string | null;
  accounts?: string[] | null; // Proper array instead of string-encoded
  excludeAccounts?: string[] | null; // Proper array instead of string-encoded
  exceptionRule?: string | null;
  scope?: string | null;
  shortDescriptionTemplate?: string | null;
  descriptionTemplate?: string | null;
  disabled: boolean;
}

/**
 * Account reference utilities
 */
export interface AccountReference {
  accountId: string;
  accountKey: string;
  name: string;
}

/**
 * Validation rule with resolved account references
 */
export interface ValidationRuleWithAccounts extends Omit<ImprovedValidationRuleDto, 'accounts' | 'excludeAccounts'> {
  accounts: AccountReference[];
  excludeAccounts: AccountReference[];
}

/**
 * Type guards for validation
 */
export const isValidAccountArray = (value: any): value is string[] => {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
};

export const isValidationRuleDto = (value: any): value is ImprovedValidationRuleDto => {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof value.clientId === 'number' &&
    (value.accounts === null || isValidAccountArray(value.accounts)) &&
    (value.excludeAccounts === null || isValidAccountArray(value.excludeAccounts))
  );
};

/**
 * Cash Movement Rule Configuration
 * Specific configuration for unexplained cash movement detection
 */
export interface CashMovementRuleConfig {
  thresholdAmount?: number;
  currency?: string;
  lookbackDays?: number;
  requireExplanation?: boolean;
  excludeSystemGenerated?: boolean;
  minimumTransactionCount?: number;
}

/**
 * Rule templates for different rule types
 */
export const RuleTemplates = {
  "unexplained-cash-movement": {
    defaultName: "Unexplained Cash Movement Detection",
    defaultShortDescription: "{count} unexplained cash movements detected",
    defaultDescription: "There are {count} unexplained cash movements totaling {amount} {currency}. Please review and confirm all cash movements have proper documentation and explanation.",
    defaultScope: "fund",
    defaultExceptionRule: JSON.stringify({
      type: "cash-movement-threshold",
      thresholdAmount: 1000,
      currency: "USD",
      lookbackDays: 30,
      requireExplanation: true,
      excludeSystemGenerated: true,
      minimumTransactionCount: 1
    } as CashMovementRuleConfig),
    category: "Financial Compliance",
    riskLevel: "High" as const
  }
} as const;
